import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import {
  Mail,
  Phone,
  ArrowRight,
  Send,
  CircleCheck,
  ShieldCheck,
  Rocket,
  Calendar,
  Clock,
  MessageSquare,
} from "lucide-react";
import { trackContact } from "../lib/analytics";
import { sendToGHL } from "../lib/ghl";
import { useSEO } from "../lib/useSEO";

const ContactPage = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    business: "",
    location: "",
    notes: "",
    consent: false,
  });
  const [submitted, setSubmitted] = useState(false);

  useSEO({
    title: "Contact — The 7 Figure Cleaner",
    description:
      "Get in touch with Chris directly. Real human replies to email the same business day, across Australia.",
    path: "/contact",
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };
  const onSubmit = (e) => {
    e.preventDefault();
    const at = new Date().toISOString();
    try {
      const queue = JSON.parse(localStorage.getItem("7fc_contact") || "[]");
      queue.push({ ...form, at });
      localStorage.setItem("7fc_contact", JSON.stringify(queue));
    } catch (_) {}
    // GoHighLevel: push the contact enquiry into the CRM via webhook.
    const [firstName, ...rest] = (form.name || "").trim().split(/\s+/);
    sendToGHL({
      source: "7fc_contact_form",
      first_name: firstName || form.name,
      last_name: rest.join(" "),
      full_name: form.name,
      email: form.email,
      phone: form.phone,
      business_name: form.business,
      service_area: form.location,
      message: form.notes,
      sms_consent: !!form.consent,
      submitted_at: at,
      page_url: typeof window !== "undefined" ? window.location.href : "",
    });
    // Meta Pixel: contact form submitted
    trackContact({ content_name: "Contact Form" });
    setSubmitted(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="flex min-h-screen flex-col overflow-x-clip bg-white text-brand-black">
      <Header />
      <main className="flex-1">
        {/* === HERO === */}
        <section className="border-b border-gray-100 bg-gradient-to-b from-blue-50/40 to-white">
          <div className="mx-auto max-w-3xl px-5 py-14 text-center sm:px-8 sm:py-20">
            <p className="mb-4 inline-flex items-center gap-2 rounded-full bg-emerald-100 px-3.5 py-1.5 text-xs font-black uppercase tracking-widest text-emerald-800">
              <MessageSquare className="h-3.5 w-3.5" /> Get In Touch
            </p>
            <h1 className="text-4xl font-black uppercase leading-[1.02] tracking-tight text-brand-black sm:text-5xl lg:text-6xl">
              Talk to a <span className="text-brand-blue">human</span> before you start.
            </h1>
            <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-[#374151] sm:text-lg">
              Prefer to talk to a human first? Chris replies to email the same business day, across Australia.
            </p>
            <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <a
                href="mailto:chris@7figureleads.com.au"
                className="inline-flex min-h-[56px] items-center justify-center gap-2 rounded-2xl bg-brand-blue px-7 py-4 text-sm font-black uppercase tracking-wider text-white shadow-xl shadow-blue-200/60 ring-1 ring-blue-700/10 transition-all hover:bg-blue-600 active:scale-[0.99]"
              >
                <Mail className="h-4 w-4" /> Email Us
              </a>
              <Link
                to="/offer"
                className="inline-flex min-h-[56px] items-center justify-center gap-2 rounded-2xl border-2 border-gray-200 bg-white px-6 py-4 text-sm font-black uppercase tracking-wider text-brand-black transition-all hover:border-brand-blue/40 hover:bg-blue-50/40"
              >
                Skip The Chat, Get Started <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <ul className="mx-auto mt-8 flex flex-wrap items-center justify-center gap-x-5 gap-y-3">
              {[
                { Icon: Calendar, t: "$250 / week + GST" },
                { Icon: ShieldCheck, t: "30 enquiries in 30 days, or free" },
                { Icon: Rocket, t: "Live in 7 days" },
              ].map(({ Icon, t }) => (
                <li key={t} className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-wide text-brand-black">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-brand-blue/10 text-brand-blue ring-1 ring-brand-blue/20">
                    <Icon className="h-3.5 w-3.5" />
                  </span>
                  {t}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* === FORM === */}
        <section className="py-14 sm:py-20">
          <div className="mx-auto grid max-w-6xl grid-cols-1 gap-10 px-5 sm:px-8 lg:grid-cols-12 lg:gap-12">
            <div className="lg:col-span-5">
              <h2 className="text-3xl font-black uppercase tracking-tight text-brand-black sm:text-4xl">
                Contact <span className="text-brand-blue">Us</span>
              </h2>
              <p className="mt-4 text-base leading-relaxed text-[#374151]">
                Send your details and a quick message. We usually reply same business day.
              </p>
              <ul className="mt-7 flex flex-col gap-3 text-sm text-brand-black sm:text-base">
                {[
                  "Ask about area availability",
                  "Check if we're a fit for your business",
                  "Get a straight answer on how it works",
                ].map((t) => (
                  <li key={t} className="flex items-center gap-2.5 font-medium">
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand-blue/10 text-brand-blue ring-1 ring-brand-blue/20">
                      <CircleCheck className="h-3 w-3" />
                    </span>
                    {t}
                  </li>
                ))}
              </ul>
            </div>

            <div className="lg:col-span-7">
              {submitted ? (
                <div className="rounded-3xl bg-white p-10 text-center shadow-sm ring-1 ring-gray-200">
                  <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700">
                    <CircleCheck className="h-8 w-8" />
                  </div>
                  <h3 className="mt-6 text-2xl font-black uppercase tracking-tight text-brand-black sm:text-3xl">
                    Message Received.
                  </h3>
                  <p className="mt-3 text-base leading-relaxed text-[#374151]">
                    Thanks {form.name?.split(" ")[0] || "mate"} — Chris will get back to you within one business day.
                  </p>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="rounded-3xl bg-white p-7 shadow-sm ring-1 ring-gray-200 sm:p-9">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <Field label="Your Name" name="name" value={form.name} onChange={onChange} placeholder="Jane Smith" required />
                    <Field label="Business Name" name="business" value={form.business} onChange={onChange} placeholder="Smith's Exterior Cleaning" />
                    <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} placeholder="you@business.com" required />
                    <Field label="Phone" name="phone" value={form.phone} onChange={onChange} placeholder="0400 000 000" />
                  </div>
                  <div className="mt-4">
                    <Field label="Service Area" name="location" value={form.location} onChange={onChange} placeholder="e.g. Sydney, NSW" />
                  </div>
                  <div className="mt-4 flex flex-col gap-1.5">
                    <label className="text-[11px] font-black uppercase tracking-wider text-brand-grayMid">
                      Your Message
                    </label>
                    <textarea
                      name="notes"
                      rows={5}
                      value={form.notes}
                      onChange={onChange}
                      required
                      placeholder="Tell us a bit about your cleaning business and how we can help..."
                      className="rounded-xl border-2 border-gray-200 bg-white px-4 py-3 text-base font-medium text-brand-black placeholder:text-gray-400 focus:border-brand-blue focus:outline-none sm:text-sm"
                    />
                  </div>
                  <label className="mt-5 flex items-start gap-3 text-xs leading-relaxed text-brand-grayMid">
                    <input
                      type="checkbox"
                      name="consent"
                      checked={form.consent}
                      onChange={onChange}
                      className="mt-0.5 h-4 w-4 shrink-0 rounded border-2 border-gray-300 text-brand-blue focus:ring-brand-blue"
                    />
                    <span>
                      I agree to receive transactional and promotional text messages (SMS) from The 7 Figure Cleaner at the number provided about my enquiry, service updates and occasional offers. Consent is not a condition of purchase. Msg frequency varies. Msg &amp; data rates may apply. Reply STOP to opt out, HELP for help.
                    </span>
                  </label>
                  <button
                    type="submit"
                    className="mt-6 inline-flex min-h-[56px] w-full items-center justify-center gap-2 rounded-2xl bg-brand-blue px-7 py-4 text-sm font-black uppercase tracking-wider text-white shadow-xl shadow-blue-200/60 ring-1 ring-blue-700/10 transition-all hover:bg-blue-600 active:scale-[0.99] sm:w-auto"
                  >
                    Send Message <Send className="h-4 w-4" />
                  </button>
                </form>
              )}
            </div>
          </div>
        </section>

        {/* === CONTACT METHODS === */}
        <section className="bg-gray-50/70 py-14 sm:py-20">
          <div className="mx-auto max-w-6xl px-5 sm:px-8">
            <div className="grid grid-cols-1 gap-5 md:grid-cols-3 md:gap-6">
              <a
                href="mailto:chris@7figureleads.com.au"
                className="group flex flex-col rounded-3xl bg-white p-7 shadow-sm ring-1 ring-gray-200 transition-all hover:shadow-md hover:ring-brand-blue/40"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-blue/10 text-brand-blue ring-1 ring-brand-blue/20">
                  <Mail className="h-5 w-5" />
                </span>
                <h3 className="mt-5 text-lg font-black uppercase tracking-tight text-brand-black">Email</h3>
                <p className="mt-2 text-sm font-bold text-brand-blue">chris@7figureleads.com.au</p>
                <p className="mt-2 text-sm leading-relaxed text-brand-grayMid">
                  Best for general questions, area availability, custom requests.
                </p>
              </a>

              <a
                href="tel:0437863673"
                className="group flex flex-col rounded-3xl bg-white p-7 shadow-sm ring-1 ring-gray-200 transition-all hover:shadow-md hover:ring-brand-blue/40"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-blue/10 text-brand-blue ring-1 ring-brand-blue/20">
                  <Phone className="h-5 w-5" />
                </span>
                <h3 className="mt-5 text-lg font-black uppercase tracking-tight text-brand-black">Phone</h3>
                <p className="mt-2 text-sm font-bold text-brand-blue">0437 863 673</p>
                <p className="mt-2 text-sm leading-relaxed text-brand-grayMid">
                  Call for urgent questions about fit, setup, or launch timing.
                </p>
              </a>

              <Link
                to="/offer"
                className="group flex flex-col rounded-3xl bg-brand-blue p-7 text-white shadow-sm ring-1 ring-brand-blue/30 transition-all hover:shadow-lg"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15 text-white ring-1 ring-white/20">
                  <Rocket className="h-5 w-5" />
                </span>
                <h3 className="mt-5 text-lg font-black uppercase tracking-tight text-white">Ready To Start</h3>
                <p className="mt-2 text-sm font-bold text-blue-100">Skip the email and check your territory.</p>
                <p className="mt-2 text-sm leading-relaxed text-blue-50">
                  $250 / week + GST. No lock-in. 30 enquiries in 30 days or we work free.
                </p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-black uppercase tracking-widest text-white">
                  Check My Territory <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                </span>
              </Link>
            </div>
          </div>
        </section>

        {/* === HOURS === */}
        <section className="py-14 sm:py-20">
          <div className="mx-auto grid max-w-6xl grid-cols-1 items-center gap-10 px-5 sm:px-8 md:grid-cols-2">
            <div>
              <h2 className="text-2xl font-black uppercase tracking-tight text-brand-black sm:text-3xl">
                Operating <span className="text-brand-blue">Hours</span>
              </h2>
              <p className="mt-4 inline-flex items-center gap-2 text-base font-medium text-brand-black sm:text-lg">
                <Clock className="h-5 w-5 text-brand-blue" /> Monday to Friday, business hours
              </p>
              <p className="mt-2 text-sm leading-relaxed text-[#374151] sm:text-base">
                Email lands the same day and we reply the same business day.
              </p>
            </div>
            <div className="rounded-3xl bg-gradient-to-br from-blue-50 to-white p-7 ring-1 ring-blue-100 sm:p-8">
              <p className="text-xs font-black uppercase tracking-widest text-brand-blue">
                30-Day Guarantee
              </p>
              <h3 className="mt-2 text-2xl font-black uppercase tracking-tight text-brand-black">
                30 Leads in 30 Days
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-[#374151]">
                Or we work free until you get them. No contracts. Cancel anytime.
              </p>
              <Link
                to="/offer"
                className="mt-5 inline-flex min-h-[52px] items-center justify-center gap-2 rounded-2xl bg-brand-blue px-6 py-3.5 text-sm font-black uppercase tracking-wider text-white shadow-lg shadow-blue-200/60 ring-1 ring-blue-700/10 transition-all hover:bg-blue-600 active:scale-[0.99]"
              >
                Check My Territory <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

const Field = ({ label, name, value, onChange, placeholder, required, type = "text" }) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-[11px] font-black uppercase tracking-wider text-brand-grayMid">
      {label}
      {required && <span className="ml-0.5 text-brand-blue">*</span>}
    </label>
    <input
      required={required}
      name={name}
      type={type}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      className="min-h-[48px] rounded-xl border-2 border-gray-200 bg-white px-4 py-3 text-base font-medium text-brand-black placeholder:text-gray-400 focus:border-brand-blue focus:outline-none sm:text-sm"
    />
  </div>
);

export default ContactPage;
